/**
 * Where the family key lives between page loads.
 *
 * IndexedDB, holding the `CryptoKey` object itself rather than its bytes.
 * IndexedDB structured-clones a CryptoKey, so the key survives a reload and a
 * browser restart without this module ever handling raw key material.
 *
 * It is worth being exact about what that does and does not buy, because an
 * earlier version of this comment overclaimed. The key is EXTRACTABLE -- it
 * has to be, or a parent could never invite another parent or link the wall
 * display; see the note in familyKey.ts. So this is not an XSS defence: script
 * running on this origin can open the same database and export the same key.
 *
 * What it does buy is that the key is never a string. It cannot land in a log
 * line, a stack trace, a crash report, `JSON.stringify` of some state object,
 * or `localStorage` where every future line of code can see it by accident.
 * The accidental paths are the ones that actually leak secrets in practice,
 * and this closes them. The deliberate path is closed by the CSP instead.
 *
 * Scoped per family, because a parent serving several families holds several
 * keys and must never use one to try to read another.
 */

import { currentAccount } from '../auth/cognito';

const DB_NAME = 'valley';
const DB_VERSION = 1;
const STORE = 'family-keys';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open local storage.'));
  });
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = work(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('Local storage failed.'));
        tx.oncomplete = () => db.close();
      }),
  );
}

/**
 * Where one ACCOUNT's key for one family is kept: `<sub>:<familyId>`.
 *
 * Per account since 2026-10-04. Before, keys were per family and a different
 * parent signing in on this browser wiped them all -- so two parents sharing
 * a computer could never both stay unlocked, and after the family started
 * over, one parent could not read the new code off Settings and then switch
 * to the other to type it in: switching had just thrown the first one's key
 * away. Kept per account, each parent unlocks once and stays unlocked, and a
 * parent who is not in a family never finds that family's key under their
 * own name.
 *
 * No session (a test, or the sign-in screens) falls back to the bare family
 * id, which is also where every key written before this lived.
 */
function slot(familyId: string): string {
  const sub = currentAccount()?.sub;
  return sub ? `${sub}:${familyId}` : familyId;
}

export async function rememberKey(familyId: string, key: CryptoKey): Promise<void> {
  await run('readwrite', (store) => store.put(key, slot(familyId)));
}

export async function recallKey(familyId: string): Promise<CryptoKey | null> {
  const mine = slot(familyId);
  try {
    const key = await run<CryptoKey | undefined>('readonly', (store) => store.get(mine));
    if (key) return key;
    if (mine === familyId) return null;
    // A key from before keys were per account. The old rule wiped every key
    // whenever a different parent signed in, so one left here belongs to the
    // last parent who did -- who is the one asking now. Move it under them.
    const old = await run<CryptoKey | undefined>('readonly', (store) => store.get(familyId));
    if (!old) return null;
    await run('readwrite', (store) => store.put(old, mine));
    await run('readwrite', (store) => store.delete(familyId));
    return old;
  } catch {
    // A private window, blocked site data, or a browser that has cleared it.
    // Not an error worth showing: it just means asking for the family code.
    return null;
  }
}

export async function forgetKey(familyId: string): Promise<void> {
  try {
    await run('readwrite', (store) => store.delete(slot(familyId)));
  } catch {
    // Nothing to do. Signing out should not fail because a delete did.
  }
}

/**
 * Every key this device holds, dropped.
 *
 * NOT called on sign-out any more, which is the correction worth recording
 * here. It was, and it meant a parent who signed out on their own phone was
 * locked out of every name in their family unless they still had a phrase
 * they had been shown once.
 *
 * The reasoning for the wipe was that the next person to use the browser
 * would inherit the key. They inherit something that reads nothing: the key
 * decrypts ciphertext, and fetching any ciphertext needs a Cognito session
 * they do not have. It also was not consistent -- a parent who never signs
 * out keeps the key here indefinitely.
 *
 * Now called only where somebody means it: "Forget my family key on this
 * device" in Settings, and account deletion.
 */
export async function forgetEverything(): Promise<void> {
  try {
    await run('readwrite', (store) => store.clear());
  } catch {
    /* as above */
  }
}
